import { useState, useEffect, useCallback } from 'react';
import { getDefaultSystemPrompt as getDefaultSystemPromptUtil, buildSystemPromptFromTemplate, getTemplateDefaultSettings, getTemplateExampleTags } from '../../utils/llm-generator/promptBuilder';
import { Logger } from '../../utils/logger';
import { llmStorage } from '../../services/storageService';

const log = Logger.create('useLLMConfig');
const API_KEY_STORAGE_PREFIX = 'llm_api_key_';
const SETTINGS_KEY = 'llmGeneratorSettings';

/**
 * Generate or retrieve device-specific encryption salt
 */
const getEncryptionSalt = () => {
  let salt = llmStorage.get('encryption_salt');
  
  if (!salt) {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    salt = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    llmStorage.set('encryption_salt', salt);
  }
  
  return salt;
};

/**
 * Decrypt API key
 */
const decryptApiKey = (encrypted, salt) => {
  if (!encrypted) return '';
  try {
    const decoded = encrypted.split('').map((char, i) =>
      String.fromCharCode(char.charCodeAt(0) ^ salt.charCodeAt(i % salt.length))
    ).join('');
    return atob(decoded);
  } catch {
    return '';
  }
};

/**
 * Custom Hook für LLM Provider Konfiguration
 * 
 * Verwaltet:
 * - LLM Provider (openai, anthropic, groq, openrouter, custom, clipboard)
 * - API Keys (verschlüsselt in localStorage)
 * - Model-Einstellungen (temperature, maxTokens etc.)
 * - Template System
 */
export function useLLMConfig() {
  // LLM Provider
  const [llmProvider, setLlmProvider] = useState('openai');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  
  // Generation Settings
  const [temperature, setTemperature] = useState(0.5);
  const [maxTokens, setMaxTokens] = useState(8192);
  const [useStructuredOutputs, setUseStructuredOutputs] = useState(false);
  const [enableJsonRepair, setEnableJsonRepair] = useState(true);
  const [useConversationHistory, setUseConversationHistory] = useState(false);
  
  // Prompts
  const [systemPrompt, setSystemPrompt] = useState(getDefaultSystemPromptUtil());
  const [selectedTemplateId, setSelectedTemplateId] = useState('coding');
  
  // Load settings from localStorage on mount
  useEffect(() => {
    const settings = llmStorage.get('generatorSettings');
    if (settings) {
      try {
        if (settings.llmProvider) setLlmProvider(settings.llmProvider);
        if (settings.baseUrl) setBaseUrl(settings.baseUrl);
        if (settings.model) setModel(settings.model);
        if (settings.temperature !== undefined) setTemperature(settings.temperature);
        if (settings.maxTokens) setMaxTokens(settings.maxTokens);
        if (settings.useStructuredOutputs !== undefined) setUseStructuredOutputs(settings.useStructuredOutputs);
        if (settings.enableJsonRepair !== undefined) setEnableJsonRepair(settings.enableJsonRepair);
        if (settings.useConversationHistory !== undefined) setUseConversationHistory(settings.useConversationHistory);
        if (settings.systemPrompt) setSystemPrompt(settings.systemPrompt);
        if (settings.selectedTemplateId) setSelectedTemplateId(settings.selectedTemplateId);
      } catch (e) {
        log.error('Error loading LLM settings:', e);
      }
    }
  }, []);
  
  // Load encrypted API key when provider changes
  useEffect(() => {
    const salt = getEncryptionSalt();
    const encrypted = llmStorage.get(`api_key_${llmProvider}`);
    
    if (encrypted) {
      const decrypted = decryptApiKey(encrypted, salt);
      if (decrypted) {
        setApiKey(decrypted);
      }
    } else {
      setApiKey('');
    }
  }, [llmProvider]);
  
  // Save settings
  const saveSettings = useCallback(() => {
    const settings = {
      llmProvider,
      baseUrl,
      model,
      temperature,
      maxTokens,
      useStructuredOutputs,
      enableJsonRepair,
      useConversationHistory,
      systemPrompt,
      selectedTemplateId
    };
    llmStorage.set('generatorSettings', settings);
  }, [llmProvider, baseUrl, model, temperature, maxTokens, useStructuredOutputs, enableJsonRepair, useConversationHistory, systemPrompt, selectedTemplateId]);
  
  // Auto-save settings when they change
  useEffect(() => {
    const timer = setTimeout(() => {
      saveSettings();
    }, 500);
    return () => clearTimeout(timer);
  }, [saveSettings]);
  
  // Handle template selection
  const handleTemplateSelect = useCallback((templateId, topicTags, setTopicTags, setIncludeCodeBlocks) => {
    setSelectedTemplateId(templateId);
    const newPrompt = buildSystemPromptFromTemplate(templateId);
    setSystemPrompt(newPrompt);
    
    const templateSettings = getTemplateDefaultSettings(templateId);
    if (templateSettings.includeCodeBlocks !== undefined && setIncludeCodeBlocks) {
      setIncludeCodeBlocks(templateSettings.includeCodeBlocks);
    }
    
    if (topicTags?.length === 0 && setTopicTags) {
      const exampleTags = getTemplateExampleTags(templateId);
      if (exampleTags.length > 0) {
        setTopicTags(exampleTags.slice(0, 3));
      }
    }
  }, []);
  
  // Reset to defaults
  const resetConfig = useCallback(() => {
    setLlmProvider('openai');
    setBaseUrl('');
    setModel('');
    setTemperature(0.5);
    setMaxTokens(8192);
    setUseStructuredOutputs(false);
    setEnableJsonRepair(true);
    setUseConversationHistory(false);
    setSystemPrompt(getDefaultSystemPromptUtil());
    setSelectedTemplateId('coding');
  }, []);
  
  return {
    // State
    llmProvider,
    apiKey,
    baseUrl,
    model,
    temperature,
    maxTokens,
    useStructuredOutputs,
    enableJsonRepair,
    useConversationHistory,
    systemPrompt,
    selectedTemplateId,
    
    // Setters
    setLlmProvider,
    setApiKey,
    setBaseUrl,
    setModel,
    setTemperature,
    setMaxTokens,
    setUseStructuredOutputs,
    setEnableJsonRepair,
    setUseConversationHistory,
    setSystemPrompt,
    setSelectedTemplateId,
    
    // Actions
    handleTemplateSelect,
    resetConfig,
    saveSettings
  };
}

export default useLLMConfig;
